import {
    collection,
    query,
    getDocs,
    setDoc,
    doc,
    writeBatch,
    QueryDocumentSnapshot,
    DocumentData,
    where,
    updateDoc,
} from 'firebase/firestore';
import { db } from '../../../config/firebase';
import DbCollections from '../../../resources/enums/DbCollections';
import { Stage, Mission, Skill } from '../types';
import MissionType from '../../../resources/enums/MissionTypes';
import { autoId } from '../../../utils/UidGenerator';

const createStageZero = (stagesTitle: object) => {
    let name = {};
    Object.entries(stagesTitle).forEach((value) => (name = { ...name, [value[0]]: `${value[1]} 0` }));
    return {
        id: autoId(),
        name: name,
        order: 0,
    } as Stage;
};

const createExitSurveyStage = (order: number, stagesTitle: object) => {
    let name = {};
    Object.entries(stagesTitle).forEach((value) => (name = { ...name, [value[0]]: `Exit survey` }));
    return {
        id: autoId(),
        name: name,
        order: order,
        stageExitSurvey: true,
    };
};

const addSkills = async (skills: Skill[]) => {
    const batch = writeBatch(db);
    skills.forEach((skill) => {
        const docRef = doc(db, DbCollections.Skills, skill.id);
        batch.set(docRef, skill);
    });
    await batch.commit();
};

const mapSnapshotToMission = (doc: QueryDocumentSnapshot<DocumentData>) => {
    return {
        ...doc.data(),
        id: doc.id,
        skills: doc.data().skills.map((skill: string) => {
            return { id: skill };
        }),
        publishFrom: doc.data().publishFrom ? doc.data().publishFrom.toDate() : null,
        publishTo: doc.data().publishTo ? doc.data().publishTo.toDate() : null,
        isSurveyDifferent: doc.data().isSurveyDifferent,
    } as Mission;
};

const mapMissionToSnapshot = (mission: Mission) => {
    return {
        ...mission,
        image: mission.image ?? '',
        publishFrom: mission.publishFrom ?? null,
        publishTo: mission.publishTo ?? null,
        reward: mission.rewards ?? [],
        type: mission.type ?? MissionType.Main,
        skills: mission.skills.map((skill) => {
            return skill.id;
        }),
        isSurveyDifferent: mission.isSurveyDifferent ?? false,
    };
};

const mapStageToSnapshot = (stage: Stage) => {
    return {
        ...stage,
        image: stage.image ?? '',
        surveyDescription: stage.surveyDescription ?? {},
        description: stage.description ?? {},
        stageExitSurvey: stage.stageExitSurvey ?? false,
        reports: stage.reports ?? [],
        valueGroups: stage.valueGroups ?? [],
        maxPoints: stage.maxPoints ?? null,
    };
};

const getSkills = async (skillsIds: string[]) => {
    if (skillsIds.length === 0) return [];
    const querySnapshot = await getDocs(query(collection(db, DbCollections.Skills), where('id', 'in', skillsIds)));
    const skills = [] as Skill[];
    querySnapshot.forEach((doc) => {
        skills.push({
            id: doc.id,
            image: doc.data().image,
            name: doc.data().name,
        });
    });
    return skills;
};

const getStages = async (missionId: string) => {
    const querySnapshot = await getDocs(query(collection(db, DbCollections.Missions, missionId, DbCollections.Stages)));
    const stages = [] as Stage[];
    querySnapshot.forEach((doc) => {
        stages.push({
            id: doc.id,
            image: doc.data().image,
            name: doc.data().name,
            description: doc.data().description,
            surveyDescription: doc.data().surveyDescription,
            exercises: doc.data().exercises,
            order: doc.data().order,
            stageExitSurvey: doc.data().stageExitSurvey,
            reports: doc.data().reports,
            valueGroups: doc.data().valueGroups,
            maxPoints: doc.data().maxPoints,
        });
    });
    return stages.sort((a, b) => a.order - b.order);
};

export const getMissionsFirebase = async () => {
    const querySnapshot = await getDocs(query(collection(db, DbCollections.Missions)));
    let missions = [] as Mission[];
    querySnapshot.forEach(async (doc) => {
        missions.push(mapSnapshotToMission(doc));
    });
    missions = await Promise.all(
        missions.map(
            async (mission) =>
                ({
                    ...mission,
                    skills: await getSkills(
                        mission.skills.map((skill) => {
                            return skill.id;
                        }),
                    ),
                    stages: await getStages(mission.id),
                } as Mission),
        ),
    );

    return missions;
};

export const saveMissionFirebase = async (
    mission: Mission,
    skills: Skill[],
    isNewMission: boolean,
    isSomeSurveyExit: boolean,
) => {
    await addSkills(skills);
    mission = {
        ...mission,
        skills: skills,
    };
    await setDoc(doc(db, DbCollections.Missions, mission.id), mapMissionToSnapshot(mission));
    const MAX_CHAPTERS = 100;
    const stageZero = createStageZero(mission.stagesTitle);
    const exitSurvey = createExitSurveyStage(MAX_CHAPTERS, mission.stagesTitle);

    if (isNewMission) {
        await setDoc(
            doc(db, DbCollections.Missions, mission.id, DbCollections.Stages, stageZero.id),
            mapStageToSnapshot(stageZero),
        );
        if (mission.isSurveyDifferent) {
            await setDoc(
                doc(db, DbCollections.Missions, mission.id, DbCollections.Stages, exitSurvey.id),
                mapStageToSnapshot(exitSurvey),
            );
        }
    }
    if (!isNewMission && mission.isSurveyDifferent && !isSomeSurveyExit) {
        await setDoc(
            doc(db, DbCollections.Missions, mission.id, DbCollections.Stages, exitSurvey.id),
            mapStageToSnapshot(exitSurvey),
        );
    }
    return {
        ...mission,
        skills: skills,
        stages:
            mission.stages && mission.stages.length > 0
                ? mission.stages
                : mission.isSurveyDifferent
                ? [stageZero, exitSurvey]
                : [stageZero],
    };
};

export const saveStageFirebase = async (stage: Stage, missionId: string) => {
    await setDoc(doc(db, DbCollections.Missions, missionId, DbCollections.Stages, stage.id), mapStageToSnapshot(stage));
    return stage;
};

export const updateRewardsFirebase = async (rewards: string[], missionId: string) => {
    await updateDoc(doc(db, DbCollections.Missions, missionId), { rewards: rewards });
    return rewards;
};
